/**
 * Inline-editable label/value row for the firm detail panel — extracted from
 * firm-detail-panel.tsx.
 */

import { Check, Pencil, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Inline-editable label/value row mirroring the Address pattern (PR #2). Three
 * visual states: empty (✗ + "Not on file" — click whole row to start editing),
 * editing (textarea/input + Save/Cancel), filled (✓ + value + hover Edit +
 * Delete). Used by the four clearable text fields (Notifications Email, Email
 * Domains, Address, Routing Preferences) so the panel reads consistent without
 * five hand-rolled variants.
 */
export function EditableTextRow({
  label,
  value,
  placeholder,
  draft,
  setDraft,
  editing,
  pending,
  multiline,
  rows = 4,
  maxLength,
  onStart,
  onSave,
  onCancel,
  onRequestClear,
  testId,
}: {
  label: string;
  value: string | null;
  placeholder?: string;
  draft: string;
  setDraft: (next: string) => void;
  editing: boolean;
  pending: boolean;
  multiline: boolean;
  rows?: number;
  maxLength?: number;
  onStart?: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRequestClear: () => void;
  testId: string;
}) {
  if (editing) {
    return (
      <li
        className="flex flex-col gap-2 rounded-sm py-1 text-sm"
        data-testid={testId}
        data-state="editing"
      >
        <div className="flex items-center gap-3">
          <X
            className="h-4 w-4 shrink-0 text-muted-foreground opacity-0"
            aria-hidden
          />
          <span className="w-44 shrink-0 text-muted-foreground">{label}</span>
        </div>
        {multiline ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={rows}
            maxLength={maxLength}
            disabled={pending}
            autoFocus
            placeholder={placeholder}
            className="w-full resize-none rounded-sm border bg-background px-2 py-1 text-sm font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
            data-testid={`${testId}-input`}
          />
        ) : (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={maxLength}
            disabled={pending}
            autoFocus
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSave();
            }}
            className="w-full rounded-sm border bg-background px-2 py-1 text-sm font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
            data-testid={`${testId}-input`}
          />
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={pending}
            className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid={`${testId}-save`}
          >
            {pending ? "Saving…" : "Save"}
          </button>
        </div>
      </li>
    );
  }

  if (value === null) {
    return (
      <li
        className={cn(
          "group relative flex items-center gap-3 rounded-sm py-1 text-sm",
          onStart && !pending && "cursor-pointer hover:bg-accent/40",
          pending && "opacity-60",
        )}
        data-testid={testId}
        data-present="false"
        onClick={onStart}
      >
        <X
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-label="Not on file"
        />
        <span className="w-44 shrink-0 text-muted-foreground">{label}</span>
        <span
          className="italic text-muted-foreground"
          data-testid={`${testId}-missing`}
        >
          {pending ? "Saving…" : "Not on file"}
        </span>
      </li>
    );
  }

  return (
    <li
      className={cn(
        "group relative flex items-start gap-3 rounded-sm py-1 text-sm",
        pending && "opacity-60",
      )}
      data-testid={testId}
      data-present="true"
    >
      <Check
        className="mt-0.5 h-4 w-4 shrink-0 text-success"
        aria-label="On file"
      />
      <span className="mt-0.5 w-44 shrink-0 text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 font-medium text-foreground",
          multiline && "whitespace-pre-line",
        )}
        data-testid={`${testId}-value`}
      >
        {value}
      </span>
      {onStart ? (
        <div className="ml-auto mt-0.5 flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={onStart}
            disabled={pending}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Edit ${label}`}
            data-testid={`${testId}-edit`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onRequestClear}
            disabled={pending}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={`Delete ${label}`}
            data-testid={`${testId}-delete`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
    </li>
  );
}
